import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import {
  COMPANY_NAME,
  SITE_DOMAIN,
  SITE_NAME,
  SITE_URL,
} from '../constants/site';

const UPDATED = 'May 24, 2026';

export default function TermsPage() {
  return (
    <PolicyLayout title="Terms & Conditions" lastUpdated={UPDATED}>
      <PolicySection title="1. Acceptance of these terms">
        <p>
          These Terms &amp; Conditions govern your access to and use of {SITE_NAME} ({SITE_URL}, &ldquo;{SITE_DOMAIN}
          &rdquo;), operated by {COMPANY_NAME}. By using our website, searching travel options, submitting booking
          requests, or contacting support, you agree to be bound by these terms.
        </p>
        <p>
          If you do not agree to these terms, you must stop using {SITE_DOMAIN}. Please also review our{' '}
          <Link to="/flight/privacy">Privacy Policy</Link> and <Link to="/flight/cookies">Cookie Policy</Link>, which explain how we
          process personal data and cookies.
        </p>
      </PolicySection>

      <PolicySection title="2. Eligibility and account responsibilities">
        <p>
          You represent that you are at least eighteen (18) years old and have legal authority to enter into binding
          agreements for yourself and, where applicable, other travelers included in a booking.
        </p>
        <PolicyList
          items={[
            'You must provide complete and accurate traveler information, including names exactly as shown on government identification.',
            'You are responsible for safeguarding account credentials and for all activities conducted under your account.',
            'You agree to use this website only for lawful travel searches and bookings, and not for speculative, false, or fraudulent reservations.',
          ]}
        />
      </PolicySection>

      <PolicySection title="3. Our role and supplier terms">
        <p>
          {COMPANY_NAME} acts as an intermediary between you and independent travel suppliers, including airlines,
          hotels, rental car companies, cruise lines, package providers, and technology systems that distribute travel
          inventory.
        </p>
        <p>
          Your travel contract is with the applicable supplier, not with {SITE_NAME}, except for services that we
          explicitly provide directly. Supplier terms, conditions, fare rules, baggage policies, cancellation windows,
          check-in requirements, and service limitations apply to your booking and may vary by supplier.
        </p>
      </PolicySection>

      <PolicySection title="4. Pricing, availability, and quotations">
        <PolicyList
          items={[
            'Displayed prices are generally shown in USD unless otherwise stated and are subject to change without notice.',
            'Search results reflect availability at the time displayed; inventory can change before ticketing or final confirmation.',
            'Final pricing may include base fare, taxes, carrier-imposed charges, service fees, processing charges, and optional add-ons selected during checkout.',
            'Obvious pricing or typographical errors may be corrected, and affected reservations may be canceled with an appropriate remedy where required by law.',
            'Promotions and discount codes are subject to separate terms, eligibility, blackout dates, and availability limits.',
          ]}
        />
      </PolicySection>

      <PolicySection title="5. Booking requests and confirmations">
        <p>
          A booking is not confirmed until you receive a confirmation email containing a valid itinerary reference,
          booking code, or e-ticket number (as applicable). Submitting payment details constitutes a request to purchase
          travel services and authorizes us and/or our payment partners to process charges.
        </p>
        <PolicySubsection title="5.1 Traveler details">
          <p>
            You are responsible for verifying all booking details before payment, including travel dates, passenger
            names, route, cabin class, and contact information. Name corrections may not be permitted after ticketing
            and can result in penalties or cancellation.
          </p>
        </PolicySubsection>
        <PolicySubsection title="5.2 Travel documents and entry requirements">
          <p>
            You are solely responsible for passports, visas, transit requirements, vaccinations, and compliance with
            all destination or transit-country entry rules. We are not liable for denied boarding or entry due to
            missing or invalid documents.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="6. Payments and fraud prevention">
        <p>
          Payments are processed by secure third-party processors. By completing a booking, you warrant that the payment
          method used is valid and that you are authorized to use it.
        </p>
        <PolicyList
          items={[
            'We may perform identity, card, and fraud checks before confirmation.',
            'If a transaction is flagged as high risk, we may request additional verification or cancel the request.',
            'Chargebacks or payment disputes may trigger booking suspension and/or additional documentation requests.',
          ]}
        />
      </PolicySection>

      <PolicySection title="7. Changes, cancellations, and refunds">
        <p>
          Change and cancellation rights depend on fare rules and supplier policies. Many promotional or low-cost fares
          are non-refundable and may not allow itinerary changes.
        </p>
        <PolicyList
          items={[
            'Refund amounts, where available, may exclude service fees, processing fees, and non-refundable components.',
            'Supplier-approved refunds are generally returned to the original payment method and can take several billing cycles.',
            'Post-ticketing assistance requested through our support team may include additional service fees disclosed at the time of request.',
            'No-show travel segments can result in cancellation of remaining itinerary portions by the supplier.',
          ]}
        />
      </PolicySection>

      <PolicySection title="8. Schedule changes and disruptions">
        <p>
          Travel suppliers may change schedules, aircraft, room allocations, ports, pickup locations, or itinerary
          elements after booking. We will make commercially reasonable efforts to notify you when updates are provided to us.
        </p>
        <p>
          Responsibility for accommodation, rebooking, compensation, denied boarding support, and disruption handling
          remains with the supplier under its own policies and applicable laws.
        </p>
      </PolicySection>

      <PolicySection title="9. Prohibited activities">
        <PolicyList
          items={[
            'Using automated tools, bots, scraping, or other methods to access or copy website content without authorization.',
            'Attempting to bypass security, interfere with system operation, or submit malicious code.',
            'Creating false bookings, abusing promotions, or engaging in payment fraud.',
            'Misrepresenting identity or traveler information.',
          ]}
        />
      </PolicySection>

      <PolicySection title="10. Intellectual property">
        <p>
          All website content, trademarks, logos, text, graphics, and software are owned by or licensed to {COMPANY_NAME}
          and protected by applicable intellectual property laws. You receive a limited, non-exclusive, revocable license
          to use this site for personal, non-commercial travel booking purposes.
        </p>
      </PolicySection>

      <PolicySection title="11. Disclaimer of warranties">
        <p>
          The website and services are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the
          fullest extent permitted by law, we disclaim all warranties, express or implied, including merchantability,
          fitness for a particular purpose, and non-infringement.
        </p>
      </PolicySection>

      <PolicySection title="12. Limitation of liability">
        <p>
          To the maximum extent permitted by law, {COMPANY_NAME} and its affiliates are not liable for indirect,
          incidental, special, consequential, or punitive damages, including lost profits, lost data, missed travel, or
          business interruption arising from use of the site or supplier-provided travel services.
        </p>
        <p>
          Our aggregate liability for claims related to a booking is limited to amounts paid directly to us as service
          fees for that booking, except where a greater limit is required by applicable law.
        </p>
      </PolicySection>

      <PolicySection title="13. Indemnification">
        <p>
          You agree to indemnify and hold harmless {COMPANY_NAME}, its officers, employees, and affiliates from claims,
          liabilities, losses, and expenses (including reasonable attorneys&apos; fees) arising out of your breach of
          these terms, violation of law, or misuse of the services.
        </p>
      </PolicySection>

      <PolicySection title="14. Governing law and dispute resolution">
        <p>
          These terms are governed by the laws of the State of Wyoming, USA, without regard to conflict-of-law
          principles. Any dispute arising out of or related to these terms or your use of the services shall be brought
          in courts of competent jurisdiction in Wyoming, unless mandatory consumer protection laws in your place of
          residence provide otherwise.
        </p>
      </PolicySection>

      <PolicySection title="15. Changes to these terms">
        <p>
          We may update these Terms &amp; Conditions at any time by posting a revised version on this page. The
          &ldquo;Last updated&rdquo; date reflects the latest revision. Continued use of {SITE_DOMAIN} after updates are
          posted constitutes acceptance of the revised terms.
        </p>
      </PolicySection>

      <PolicySection title="16. Contact information">
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
